import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useRouter } from 'vue-router';
import { authAPI, teamsAPI } from '../services/api';

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null);
  const teams = ref([]);
  const currentTeam = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const router = useRouter();

  // 检查用户是否已登录
  const isLoggedIn = computed(() => !!user.value);

  // 获取当前团队
  const getCurrentTeam = computed(() => currentTeam.value);

  // 注册
  const register = async (userData) => {
    try {
      loading.value = true;
      error.value = null;
      
      const response = await authAPI.register(userData);
      user.value = response.user;
      
      // 注册成功后获取团队信息
      await fetchTeams();
      
      router.push('/');
      return response;
    } catch (err) {
      error.value = err.response?.data?.message || '注册失败';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // 登录
  const login = async (credentials) => {
    try {
      loading.value = true;
      error.value = null;
      
      const response = await authAPI.login(credentials);
      user.value = response.user;
      
      // 登录成功后获取团队信息
      await fetchTeams();
      
      router.push('/');
      return response;
    } catch (err) {
      error.value = err.response?.data?.message || '登录失败';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // 登出
  const logout = () => {
    authAPI.logout();
    user.value = null;
    teams.value = [];
    currentTeam.value = null;
    router.push('/login');
  };

  // 验证token
  const verifyAuth = async () => {
    try {
      const response = await authAPI.verify();
      user.value = response.user;
      await fetchTeams();
      return response.user;
    } catch (err) {
      authAPI.logout();
      user.value = null;
      return null;
    }
  };

  // 获取用户团队
  const fetchTeams = async () => {
    try {
      const response = await teamsAPI.getMyTeams();
      teams.value = response;
      
      // 如果没有当前团队且有团队，设置第一个为当前团队
      if (!currentTeam.value && teams.value.length > 0) {
        currentTeam.value = teams.value[0];
      }
    } catch (err) {
      console.error('获取团队失败:', err);
    }
  };

  // 创建团队
  const createTeam = async (teamData) => {
    try {
      loading.value = true;
      error.value = null;
      
      const response = await teamsAPI.createTeam(teamData);
      teams.value.push(response);
      currentTeam.value = response;
      
      return response;
    } catch (err) {
      error.value = err.response?.data?.message || '创建团队失败';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // 加入团队
  const joinTeam = async (inviteCode) => {
    try {
      loading.value = true;
      error.value = null;
      
      const response = await teamsAPI.joinTeam(inviteCode);
      teams.value.push(response);
      
      return response;
    } catch (err) {
      error.value = err.response?.data?.message || '加入团队失败';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // 切换当前团队
  const switchTeam = (team) => {
    currentTeam.value = team;
  };

  // 获取团队邀请码
  const getInviteCode = computed(() => {
    return currentTeam.value?.inviteCode || '';
  });

  // 获取团队成员
  const getTeamMembers = async (teamId) => {
    try {
      loading.value = true;
      error.value = null;
      
      const response = await teamsAPI.getTeamMembers(teamId);
      return response;
    } catch (err) {
      error.value = err.response?.data?.message || '获取团队成员失败';
      console.error(error.value);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    user,
    teams,
    currentTeam,
    loading,
    error,
    isLoggedIn,
    getCurrentTeam,
    getInviteCode,
    getTeamMembers,
    register,
    login,
    logout,
    verifyAuth,
    fetchTeams,
    createTeam,
    joinTeam,
    switchTeam
  };
});